import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { useHistory } from 'react-router-dom';
import _ from 'lodash'
import { format, sub, eachDayOfInterval } from 'date-fns'

import { Chart, Tabs } from 'components'
import { PageBlock, ItemBlock, ItemModal, DayModal } from 'features'
import { AnimatePresence, motion } from 'framer-motion';

import * as actionCreators from 'store/actions'
import * as api from 'api'
import { dark, light } from 'theme'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
`

const ChartContainer = styled.div`
    border-radius: 5px;
    width: 100%;
    height: 20rem;
    position: relative;
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
`

const Placeholder = styled.div`
    color: var(--secondary-accent-color);
    margin: 0.5rem 0.5rem;
    font-size: 0.8rem;
`

const Title = styled.div`
    font-size: 1.5rem;
    font-weight: bold;
    /* margin-left: 0.5rem; */
    margin: 1rem 0.5rem 0.5rem 0.5rem;
`

const Tab = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`

const Indicator = styled.div`
    font-size: 0.8rem;
    margin-left: 0.5rem;
    /* color: var(--secondary-accent-color); */
    font-weight: bolder;
`

const Body = styled.div`
    height: 100%;
    width: 100%;
    flex-grow: 4;
    overflow-x: hidden;
    min-height: 30vh;
`

const Wrapper = styled.div`
    width: 200%;
    height: inherit;
    display: grid;
    grid-template-columns: 50% 50%;
    overflow: hidden;
`

const HistoryModule = props => {
    const {} = props;
    const labelFormat = 'ddd,D-MMM'
    const dispatch = useDispatch()
    let history = useHistory()
    const user = useSelector(state => state.auth.user)
    const themeRef = user['theme'] === 'Dark' ? dark : light
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const [scheduledData, setScheduled] = React.useState({})
    const [completedData, setCompleted] = React.useState({})
    const [date, setDate] = React.useState(moment().format(labelFormat))
    const [tab, setTab] = React.useState('COMPLETED')
    const [labels, setLabels] = React.useState(() => {
        const start = sub(new Date(), {months: 1})
        const end = new Date()
        const interval = eachDayOfInterval({ start: start, end: end })
        // return interval.map(date => format(date, 'yyyy-MM-dd'))
        return interval.map(date => moment(date).format(labelFormat))
    })

    React.useEffect(() => {
        // Get chart data
        api.countItems({ userId: user['uid']})
        .then(countRes => {
            const outcome = countRes.data['outcome']
            delete outcome['null']
            console.log('Scheduled', outcome)
            let scheduled = {}
            for (const [key, value] of Object.entries(outcome)) {
                scheduled[moment(key).format(labelFormat)] = value
            }
            setScheduled(scheduled)
        })
        api.countItems({ userId: user['uid'], completed: true})
        .then(countRes => {
            const outcome = countRes.data['outcome']
            delete outcome['null']
            console.log('Completed', outcome)
            let completed = {}
            for (const [key, value] of Object.entries(outcome)) {
                completed[moment(key).format(labelFormat)] = value
            }
            setCompleted(completed)
        })

        // Get items
        dispatch(actionCreators.loadItems(data, {
            "logic": "OR",
            userID: user['uid'],
            "filters": {
                end_date: {
                    "$gte": moment().subtract(30, 'days').toDate(),
                    "$lte": moment().toDate()
                },
                completed_date: {
                    "$gte": moment().subtract(30, 'days').toDate(),
                    "$lte": moment().toDate()
                }
            }
        }))
    }, [])

    const onSelectPage = (pageID) => {
        const oldUser = {...user}
        let updatedUser = {...user}
        updatedUser['activePageID'] = pageID
        updatedUser['activePageType'] = 'Pages'
        dispatch(actionCreators.updateUser(updatedUser, oldUser))
        history.push(pageID.toLowerCase())
        window.scrollTo(0,0)
    }

    const onSelectChart = ({ label, index, values }) => {
        const selected = labels[index]
        setDate(selected)
    }

    const getChart = () => {
        let ref = {
            scheduled: scheduledData || {},
            completed: completedData || {}
        }
        let data = {
            scheduled: [],
            completed: []
        }
        labels.forEach(date => {
            Object.keys(data).forEach(key => {
                data[key].push(ref[key][date] ? ref[key][date] : 0)
            })
        })
        console.log({ref, data, labels})

        return <Chart type='LINE' theme={user['theme']} height='20rem'
                labels={labels}
                onSelect={onSelectChart}
                data={[
                    {
                        label: 'completed',
                        data: data['completed'],
                        borderColor: themeRef['--focus-color'],
                        backgroundColor: themeRef['--focus-color'],
                        yMax: Math.floor((Math.max(...data['scheduled']) + 10) / 10) * 10,
                    },
                    {
                        label: 'scheduled',
                        data: data['scheduled'],
                        // borderColor: themeRef['--primary-accent-color'],
                        // backgroundColor: themeRef['--primary-accent-color'],
                        borderColor: themeRef['--focus-color2'],
                        backgroundColor: themeRef['--focus-color2'],
                        yMax: Math.floor((Math.max(...data['scheduled']) + 10) / 10) * 10,
                    },
                ]}/>
    }

    const getBlocks = () => {
        // Blocks
        let blocks = {
            COMPLETED: [],
            SCHEDULED: []
        }
        Object.keys(blocks).forEach(key => {
            const feature = key === 'COMPLETED' ? 'completed_date' : 'end_date'
            const items = Object.values(data['items']).filter(item => item[feature] && moment(item[feature]).format(labelFormat) === date)
            const pageIDs = _.uniq(items.map(item => item['pageID']))
            pageIDs.forEach(pageID => {
                blocks[key].push(
                    <PageBlock key={pageID} page={data['pages'][pageID]} disableButtons
                        style={{size: '1.2rem', fontSize: '1.2rem', padding: '0.5rem', backgroundColor: 'var(--secondary-color)', borderRadius: '5px', borderBottom: '1px solid var(--secondary-color)'}}
                        onSelect={() => onSelectPage(pageID)}/>
                )

                const pageItems = items.filter(item => item['pageID'] === pageID)
                pageItems.forEach(item => {
                    blocks[key].push(
                        <ItemBlock type='VIEW2' key={item.itemID} itemID={item['itemID']} 
                            primaryColor='var(--secondary-color)' 
                            onSelect={() => dispatch(actionCreators.setModal(<ItemModal itemID={item['itemID']}/>, `${item.itemID}_ItemModal`))}/>
                    )
                })
            })

            // Placeholers
            if(blocks[key].length === 0) {
                blocks[key].push(<Placeholder>{`No ${key.toLowerCase()} items`}</Placeholder>)
            }
        })

        return {
            SCHEDULED: <Column>{blocks['SCHEDULED']}</Column>,
            COMPLETED: <Column>{blocks['COMPLETED']}</Column>,
        }
    }

    const blocks = getBlocks()
    return (
        <Container>
            <ChartContainer>{getChart()}</ChartContainer>

            {/* Tabs */}
            <Title>{moment(date).format('ddd, D-MMM')}</Title>
            <Tabs selected={tab} onSelect={value => setTab(value)}
                    styles={{ 
                        main: { backgroundColor: 'var(--primary-color)', width: '100%' },
                        tab: { padding: '0.5rem 0' }
                    }}
                    options={{
                        COMPLETED: <Tab>
                                        <div>Completed</div>
                                        <Indicator>{completedData[date] || 0}</Indicator>
                                    </Tab>,
                        SCHEDULED: <Tab>
                                    <div>Scheduled</div>
                                    <Indicator>{scheduledData[date] || 0}</Indicator>
                                </Tab>
                    }}
                    />
            
            {/* Items */}
            <Body>
                <AnimatePresence initial={false}>
                    <motion.div key='completed' 
                        initial={tab === 'COMPLETED' ? "right" : 'left'}
                        animate={tab === 'COMPLETED' ? "left" : 'right'}
                        exit={tab === 'COMPLETED' ? "right" : 'left'}
                        variants={{
                            left: { x: 0, height: '100%' },
                            right: { x: '-100%', height: '100%' }
                        }}
                        transition={{ duration: 0.3 }}>
                        <Wrapper>
                            {blocks['COMPLETED']}
                            {blocks['SCHEDULED']}
                        </Wrapper>
                    </motion.div>
                </AnimatePresence>
            </Body>
        </Container>
    )
}

export default HistoryModule